import PropTypes from 'prop-types';
import React from 'react';
import SplitLayout from '../../common/layouts/splitLayout';
import AudioContainer from '../../common/audio/audioContainer';
import AnswerButtonContainer from '../../common/splitPageQuestion/AnswerButton/AnswerButtonContainer';
import ScreenErrorContainer from '../../common/error/ScreenErrorContainer';
import GoButton from '../../common/components/GoButton';
import FITBQuestionInput from '../../common/components/FITBQuestionInput';
import { VOCAB } from '../../constants/measure';
import { localize, DICTIONARY_KEYS } from '../../common/localization';
import { ARIA_LABELS } from '../../constants/text';

export default class VocabFITBView extends React.Component {
    static getSelectedText(props) {
        const { possibleResponses: responses, selectedResponse } = props;

        return responses[selectedResponse] ? responses[selectedResponse].text : '';
    }

    constructor(props) {
        super(props);

        this.handleSelectResponse = this.handleSelectResponse.bind(this);
    }

    componentDidMount() {
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else {
            this.props.narrateScreen(this.props.stimulus.audio);
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.isDataFetched && this.narrationShouldStart(prevProps)) {
            this.props.narrateScreen(this.props.stimulus.audio);
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
    }

    getStimulusArea() {
        if (this.props.isDataFetched) {
            return (
                <FITBQuestionInput
                    id={this.props.id}
                    inputSize={this.props.maxResponseLength}
                    isStimulusPlaying={this.props.isStimulusPlaying}
                    selectedResponseText={VocabFITBView.getSelectedText(this.props)}
                    stimulus={this.props.stimulus}
                    playStimulus={this.playStimulus()}
                    isDisabled={this.props.isScreenGreyedOut}
                />
            );
        }

        return null;
    }

    getResponsesArea() {
        if (this.props.isDataFetched) {
            return (
                <div className="vertical-answer-buttons centered condensed text-centered">
                    {this.props.possibleResponses.map((response, index) => (
                        <AnswerButtonContainer
                            key={response.id}
                            index={index}
                            audio={response.audio}
                            text={response.text}
                            isSelected={index === this.props.selectedResponse}
                            onAnswerClick={this.handleSelectResponse(index)}
                            isDisabled={this.props.isScreenGreyedOut}
                        />
                    ))}
                </div>
            );
        }

        return null;
    }

    handleSelectResponse(response) {
        return () => {
            this.props.selectResponse(response);
        };
    }

    playStimulus() {
        return () => {
            this.props.playStimulus(this.props.stimulus.audio);
        };
    }

    narrationShouldStart(prevProps) {
        const isReady = this.props.isDataFetched;
        const shouldStartNarrate =
            prevProps.isDataFetched !== this.props.isDataFetched ||
            prevProps.questionIndex !== this.props.questionIndex;

        return isReady && shouldStartNarrate;
    }

    handleOnNext() {
        return () => {
            this.props.onNext(this.props.selectedResponse);
        };
    }

    render() {
        return (
            <div className={`${VOCAB.ASSESSMENT} ${VOCAB.FITB}`}>
                <SplitLayout
                    upperElement={this.getStimulusArea()}
                    lowerElement={this.getResponsesArea()}
                    isScreenBlocked={this.props.isScreenBlocked}
                    isGreyedOut={this.props.isScreenGreyedOut}
                    questionIndex={this.props.questionIndex}
                    enableScroll
                >
                    <AudioContainer />
                    <ScreenErrorContainer onRetry={this.handleOnNext()} />
                    {this.props.isNextButtonActive && (
                        <GoButton
                            text={localize(DICTIONARY_KEYS.OK)}
                            onClick={this.handleOnNext()}
                            isEnabled={!this.props.isScreenBlocked}
                            ariaLabel={ARIA_LABELS.GO_TO_NEXT_STEP}
                        />
                    )}
                </SplitLayout>
            </div>
        );
    }
}

VocabFITBView.propTypes = {
    fetchContent: PropTypes.func,
    narrateScreen: PropTypes.func,
    onNext: PropTypes.func,
    cleanup: PropTypes.func,
    isDataFetched: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isScreenGreyedOut: PropTypes.bool,
    isNextButtonActive: PropTypes.bool,
    stimulus: PropTypes.object,
    isStimulusPlaying: PropTypes.bool,
    selectResponse: PropTypes.func,
    playStimulus: PropTypes.func,
    selectedResponse: PropTypes.number,
    possibleResponses: PropTypes.array,
    id: PropTypes.string,
    maxResponseLength: PropTypes.number,
    questionIndex: PropTypes.number,
};

VocabFITBView.defaultProps = {
    id: '',
    isDataFetched: false,
    isScreenBlocked: false,
    isScreenGreyedOut: false,
    isNextButtonActive: false,
    isStimulusPlaying: false,
    possibleResponses: [],
    stimulus: undefined,
    selectedResponse: undefined,
    maxResponseLength: undefined,
    questionIndex: undefined,
    fetchContent: () => {},
    selectResponse: () => {},
    playStimulus: () => {},
    narrateScreen: () => {},
    onNext: () => {},
    cleanup: () => {},
};
